import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { MDBContainer, MDBRow, MDBCol, MDBBtn, MDBBtnGroup, MDBDatatable } from 'mdb-react-ui-kit'
import MDB4Datatable from '../../../../components/content/MDB4Datatable/MDB4Datatable.component'
import ProductionReportWidgetService from '../../../../shared/services/ProductionReportWidget.service'
import ComparativeDateCalculator from './ComparativeDateCalculator.class'
import { parseISO } from 'date-fns'
import './DownlineGrowthReport.scss'

// Metrics that the downline API does not support.
// When the parent LeaderWidget switches to one of these, we keep showing
// the last successfully fetched metric instead of making a failing request.
const IGNORED_METRICS = new Set(['enrolled'])

// Metrics whose values represent dollar amounts and should display as currency.
const CURRENCY_METRICS = new Set(['av', 'av_no_aca', 'life_av', 'med_supps_av'])

const formatCurrency = val =>
  '$' +
  Number(val).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

// Human-readable labels for the metric query-string values.
const METRIC_LABELS = {
  points: 'Points',
  av: 'AV',
  av_no_aca: 'AV (without ACA)',
  aca_lives: 'ACA Lives',
  group: 'Groups',
  med_adv: 'Med Advantage',
  life_av: 'Life',
  med_supps_av: 'Med Supps',
}

// Timespan options for the button group filter.
// Each key maps to a field prefix in the API response (e.g., "week_current").
const TIMESPAN_OPTIONS = [
  { key: 'week', label: 'WTD' },
  { key: 'month', label: 'MTD' },
  { key: 'quarter', label: 'QTD' },
  { key: 'year', label: 'YTD' },
]

// Calculates year-over-year growth percentage between two values.
// Returns "0.00%" when both are zero, "100.00%" when previous is zero.
const calcGrowth = (current, previous) => {
  const c = parseFloat(current) || 0
  const p = parseFloat(previous) || 0
  if (c === 0 && p === 0) return '0.00%'
  if (p === 0) return '100.00%'
  return (((c - p) / p) * 100).toFixed(2) + '%'
}

// Builds a single table row for an agent by extracting the selected timespan's
// current and previous values. Either curr or prev may be null when an agent
// only appears in one of the two API responses.
const buildRow = (curr, prev, timespan) => {
  const cVal = parseFloat(curr?.[`${timespan}_current`]) || 0
  const pVal = parseFloat(prev?.[`${timespan}_current`]) || 0
  return {
    agent_name: curr?.agent_name || prev?.agent_name || '',
    current: cVal,
    previous: pVal,
    growth: calcGrowth(cVal, pVal),
  }
}

// Correlates the current-year and previous-year API responses by agent_id.
// Both API responses return the same schema:
//   { agent_id, agent_name, week_current, month_current, quarter_current, year_current }
//
// Agents that exist in current only will show previous values as 0.
// Agents that exist in previous only will show current values as 0.
const correlateResults = (current, previous, timespan) => {
  const currByAgent = {}
  const prevByAgent = {}
  const seenIds = new Set()

  // Index both arrays by agent_id for O(1) lookup.
  if (Array.isArray(current)) {
    current.forEach(agent => {
      if (agent.agent_id) currByAgent[agent.agent_id] = agent
    })
  }
  if (Array.isArray(previous)) {
    previous.forEach(agent => {
      if (agent.agent_id) prevByAgent[agent.agent_id] = agent
    })
  }

  const rows = []

  // First pass: iterate current-year agents, matching to previous where available.
  Object.keys(currByAgent).forEach(id => {
    seenIds.add(id)
    rows.push(buildRow(currByAgent[id], prevByAgent[id], timespan))
  })

  // Second pass: pick up agents that only exist in the previous-year response.
  Object.keys(prevByAgent).forEach(id => {
    if (!seenIds.has(id)) rows.push(buildRow(null, prevByAgent[id], timespan))
  })

  return rows
}

const TABLE_COLUMNS = [
  { label: 'Agent', field: 'agent_name', sort: 'asc' },
  { label: 'Current', field: 'current', sort: 'asc' },
  { label: 'Previous', field: 'previous', sort: 'asc' },
  { label: '% Diff', field: 'growth', sort: 'asc' },
]

export default observer(() => {
  // Raw API responses stored separately so switching timespans or toggling
  // the inactive filter doesn't re-fetch from the API.
  const [rawData, setRawData] = useState({ current: [], previous: [] })
  const [timespan, setTimespan] = useState('week')
  const [fetching, setFetching] = useState(false)
  // Tracks the metric currently displayed in the table.
  const [activeMetric, setActiveMetric] = useState('points')
  // When true, agents with zero production in both current and previous are hidden.
  // Defaults to true since zero/zero agents add no value to the report.
  const [hideInactive, setHideInactive] = useState(true)

  // Derives the MDBDatatable data object from raw API data + selected timespan.
  // Applies the hideInactive filter after correlation so raw data stays intact.
  const getTableData = () => {
    let rows = correlateResults(rawData.current, rawData.previous, timespan)
    if (hideInactive) rows = rows.filter(row => row.current !== 0 || row.previous !== 0)

    // Compute totals before formatting so we sum raw numbers.
    const totalCurrent = rows.reduce((sum, r) => sum + r.current, 0)
    const totalPrevious = rows.reduce((sum, r) => sum + r.previous, 0)

    if (CURRENCY_METRICS.has(activeMetric))
      rows = rows.map(row => ({
        ...row,
        current: formatCurrency(row.current),
        previous: formatCurrency(row.previous),
      }))

    // Append a bold totals row at the bottom of the table.
    const bold = val => <strong>{val}</strong>
    rows.push({
      agent_name: bold('Totals'),
      current: bold(CURRENCY_METRICS.has(activeMetric) ? formatCurrency(totalCurrent) : totalCurrent),
      previous: bold(CURRENCY_METRICS.has(activeMetric) ? formatCurrency(totalPrevious) : totalPrevious),
      growth: bold(calcGrowth(totalCurrent, totalPrevious)),
    })

    return { columns: TABLE_COLUMNS, rows }
  }

  // Fetches downline production data for both the current and comparison periods.
  // Uses ComparativeDateCalculator to compute the two search payloads (current date
  // and previous-year equivalent) from the query string parameters.
  // Makes two parallel API calls via Promise.all to minimize load time.
  const fetch = async ({ metric, userId, reportDate, prevYear }) => {
    setFetching(true)
    setActiveMetric(metric)
    try {
      const Calc = new ComparativeDateCalculator()

      // Set the current report date from the query string.
      if (reportDate) {
        const parsed = parseISO(reportDate)
        if (!isNaN(parsed.getTime())) Calc.setCurrReportDate(parsed)
      }

      // Set the comparison year (e.g., 2025 vs default of currentYear - 1).
      // Must be called after setCurrReportDate so the year diff is calculated correctly.
      if (prevYear && !isNaN(prevYear)) Calc.setPrevReportYear(parseInt(prevYear))

      // getCurrSearch() returns { report_date: "YYYY-MM-DD" }
      // getPrevSearch() returns { report_date, week, month, quarter, year }
      // with comparison-year equivalent dates for each timespan.
      // Only include user_id when present to avoid sending "null" as a string.
      const currSearch = { ...Calc.getCurrSearch() }
      const prevSearch = { ...Calc.getPrevSearch() }
      if (userId) {
        currSearch.user_id = userId
        prevSearch.user_id = userId
      }

      // API returns nested arrays ([[...]]), so flatten to get the inner array.
      const [currentRaw, previousRaw] = await Promise.all([
        ProductionReportWidgetService.searchDownline({
          search: { ...currSearch, metric },
        }),
        ProductionReportWidgetService.searchDownline({
          search: { ...prevSearch, metric },
        }),
      ])

      const current = Array.isArray(currentRaw?.[0]) ? currentRaw[0] : currentRaw
      const previous = Array.isArray(previousRaw?.[0]) ? previousRaw[0] : previousRaw

      setRawData({ current, previous })
    } catch (ex) {
      console.log(`Failed to fetch downline report: ${ex}`)
    } finally {
      setFetching(false)
    }
  }

  // Listens for query string changes triggered by the parent LeaderWidget.
  // The LeaderWidget updates the URL via window.history.pushState (bypassing
  // React Router), so we intercept pushState/replaceState and listen for
  // popstate (back/forward navigation) to detect changes.
  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search)
      const metric = params.get('metric') || 'points'

      // The downline API does not support 'enrolled'; silently skip the fetch
      // so the table keeps showing the last valid metric's data.
      if (IGNORED_METRICS.has(metric)) return

      fetch({
        metric,
        userId: params.get('user_id'),
        reportDate: params.get('report_date'),
        prevYear: params.get('prev_year'),
      })
    }

    // Fetch on initial mount.
    handleUrlChange()

    // Browser back/forward navigation.
    window.addEventListener('popstate', handleUrlChange)

    // Intercept programmatic URL changes (pushState/replaceState)
    // used by the parent LeaderWidget's _updateQueryString().
    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    window.history.pushState = function (...args) {
      originalPushState.apply(this, args)
      handleUrlChange()
    }

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(this, args)
      handleUrlChange()
    }

    // Cleanup: restore original history methods and remove listener.
    return () => {
      window.removeEventListener('popstate', handleUrlChange)
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
    }
  }, [])

  return (
    <MDBContainer fluid id='DownlineGrowthReportComponent'>
      <MDBRow>
        <MDBCol size='12' className='mb-3'>
          <h3>Agent Growth Reports</h3>

          <div className='content-wrapper'>
            <MDBBtnGroup className='dgr-control-btns'>
              {/* Timespan filter — matches the WTD/MTD/QTD/YTD pattern from ExportLeaderGrowthReportModal. */}
              {TIMESPAN_OPTIONS.map(({ key, label }) => (
                <MDBBtn
                  type='button'
                  key={key}
                  disabled={fetching || timespan === key}
                  onClick={() => setTimespan(key)}
                  color={timespan === key ? 'blue-grey' : 'indigo'}
                >
                  {label}
                </MDBBtn>
              ))}

              {/* Hide agents with zero production in both current and previous periods. */}
              <MDBBtn
                type='button'
                size='sm'
                color={hideInactive ? 'blue-grey' : 'indigo'}
                onClick={() => setHideInactive(!hideInactive)}
              >
                {hideInactive ? 'Show' : 'Hide'} Inactive Agents
              </MDBBtn>
            </MDBBtnGroup>

            <div className='viewing-metric-label'>
              Viewing <strong>{METRIC_LABELS[activeMetric] || activeMetric}</strong>
            </div>

            {/* Sortable data table — one row per downline agent.
	              key forces a full remount when timespan or filter changes,
	              preventing MDBDatatable's internal sort state from going stale. */}
            {fetching ? (
              <div className='text-center py-4'>
                <i className='fa fa-spin fa-spinner fa-2x'></i>
              </div>
            ) : (
              <MDB4Datatable
                data={getTableData()}
                key={`${timespan}-${hideInactive}`}
                striped
                small
                bordered
                order={['agent_name', 'asc']}
                entries={250}
                entriesOptions={[250]}
                displayEntries={false}
              />
            )}
          </div>
        </MDBCol>
      </MDBRow>
    </MDBContainer>
  )
})
